import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MapPin, Heart, Facebook, Instagram, Donut, ChevronDown } from "lucide-react";
import { FULL_LOGO_URL, WOODGRAIN_IMG, FACEBOOK_URL, INSTAGRAM_URL, isJuly4DropActive } from "@/data/content";
import { Fireworks } from "@/components/Fireworks";
import { DropRibbon } from "@/components/DropRibbon";

const HERO_BURSTS = [
  { x: "12%", y: "13%", color: "#e63946", delay: 0 },
  { x: "27%", y: "6%", color: "#2f57c9", delay: 0.5 },
  { x: "42%", y: "16%", color: "#f7f7f7", delay: 1.0 },
  { x: "58%", y: "16%", color: "#2f57c9", delay: 1.5 },
  { x: "73%", y: "6%", color: "#e63946", delay: 2.0 },
  { x: "88%", y: "13%", color: "#2f57c9", delay: 2.5 },
  { x: "50%", y: "22%", color: "#e63946", delay: 3.0 },
];

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const contentRef = useRef(null);
  const [showArrow, setShowArrow] = useState(true);
  const july4Active = isJuly4DropActive();

  useEffect(() => {
    const check = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // show the arrow only when there's real room beneath the content
      setShowArrow(rect.bottom + 84 < window.innerHeight);
    };
    check();
    const t = setTimeout(check, 400);
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("resize", check);
      clearTimeout(t);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col items-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${WOODGRAIN_IMG})` }}
      />
      <div className="absolute inset-0 bg-[#f7f0eb]/75" />

      {july4Active && (
        <Fireworks
          bursts={HERO_BURSTS}
          radius={62}
          particleSize={7}
          peakOpacity={0.85}
          flash
          className="z-[5]"
          testId="hero-fireworks"
        />
      )}

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full px-6 pt-20 pb-4 sm:pt-24">
        <div
          ref={contentRef}
          className="flex flex-col items-center text-center w-full max-w-4xl"
        >
          {july4Active && <DropRibbon />}
          <motion.div
            initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 120, damping: 12 }}
          >
            <button
              data-testid="hero-logo-btn"
              onClick={() => scrollTo("truck")}
              aria-label="Watch Bobo's story — Zach IS Bobo"
              className="block rounded-full"
            >
              <div className="relative inline-block rounded-full transition-transform duration-300 ease-out hover:scale-110">
                <img
                  data-testid="hero-logo"
                  src={FULL_LOGO_URL}
                  alt="Bobo's Donuts logo"
                  className="w-[42vw] h-[42vw] sm:w-[min(19rem,30vh)] sm:h-[min(19rem,30vh)] rounded-full object-cover shadow-2xl ring-4 ring-white"
                />
                <span
                  aria-hidden="true"
                  data-testid="hero-logo-tm"
                  className="absolute bottom-[7%] right-[6%] text-[#111111]/70 text-xs sm:text-sm font-semibold select-none pointer-events-none"
                >
                  ™
                </span>
              </div>
            </button>
          </motion.div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-4 sm:mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 border border-[#e8dcc8] text-xs sm:text-sm font-semibold text-[#3d3d3d]"
          >
            <Heart className="h-4 w-4 text-[#ed4a76]" /> Hand-cut donuts with a whole lotta heart
          </motion.div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#111111] mt-4 sm:mt-5 leading-[1.05]"
          >
            Fresh, delicious &amp;{" "}
            <span className="text-[#ff648b]">made with love.</span>
            <img
              src="/asl-love-hero.png?v=2"
              alt="ASL sign for I love you"
              data-testid="hero-asl-love"
              className="inline-block h-[0.8em] w-auto ml-2 sm:ml-3 align-[-0.06em] select-none"
            />
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-base sm:text-xl text-[#3d3d3d] mt-4 sm:mt-5 max-w-2xl leading-relaxed"
          >
            A family-run donut truck rolling through Mattawan, Michigan — building
            a more inclusive community one sweet treat at a time.
          </motion.p>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-6 sm:mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              data-testid="hero-find-truck-btn"
              onClick={() => scrollTo("locations")}
              className="px-8 py-3 sm:py-4 rounded-full bg-[#ff648b] text-[#111111] font-bold text-base sm:text-lg hover:bg-[#ed4a76] hover:scale-105 transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <MapPin className="h-5 w-5" /> Find the Truck
            </button>
            <div
              data-testid="hero-social-btn"
              className="flex items-stretch rounded-full overflow-hidden bg-white/70 border-2 border-[#111111]/15 shadow-lg hover:scale-105 transition-transform"
            >
              <span className="flex items-center gap-2 px-7 py-3 sm:py-4 text-[#111111] font-bold text-base sm:text-lg whitespace-nowrap">
                <Donut className="h-5 w-5" /> Follow the Truck
              </span>
              <div className="w-px self-stretch bg-[#111111]/15" />
              <a
                data-testid="hero-facebook-link"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bobo's on Facebook"
                className="flex items-center justify-center px-6 py-3 sm:py-4 text-[#111111] hover:bg-[#1877F2] hover:text-white transition-colors"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <div className="w-px self-stretch bg-[#111111]/15" />
              <a
                data-testid="hero-instagram-link"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bobo's on Instagram"
                className="flex items-center justify-center px-6 py-3 sm:py-4 text-[#111111] hover:text-white hover:bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)] transition-colors"
              >
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {showArrow && (
        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollTo("donuts")}
          aria-label="Scroll down to see our donuts"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="hidden sm:flex self-center relative z-10 mb-6 shrink-0"
        >
          <motion.span
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ff648b] text-[#111111] shadow-lg hover:bg-[#ed4a76] transition-colors"
          >
            <ChevronDown className="h-6 w-6" strokeWidth={3} />
          </motion.span>
        </motion.button>
      )}
    </section>
  );
};
